import { FocusableElement } from "@chakra-ui/utils";
import { OutstandingFeeChargeEvent } from "gen/model/outstandingFeeChargeEvent";
import { OutstandingFeeChargeEventsByFund } from "gen/model/outstandingFeeChargeEventsByFund";
import React from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectProps } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { formatMoney, sumMoneyStrings } from "shared/utils/MoneyUtils";

// this function computes fund family level outstanding fees by summing the outstanding fees of all funds
export const getFundFamilyOutstandingFeeChargeEvents = (
  feeChargeEventsByFund: OutstandingFeeChargeEventsByFund,
): OutstandingFeeChargeEvent[] =>
  Object.values(
    Object.values(feeChargeEventsByFund.funds)
      .flat(1)
      .reduce<Record<string, OutstandingFeeChargeEvent>>(
        (fund_family_totals, outstandingEvent) => {
          const total = fund_family_totals[outstandingEvent.id] as
            | OutstandingFeeChargeEvent
            | undefined;
          return {
            ...fund_family_totals,
            [outstandingEvent.id]: {
              ...outstandingEvent,
              amount_outstanding_to_date: sumMoneyStrings([
                total?.amount_outstanding_to_date ?? "0.00",
                outstandingEvent.amount_outstanding_to_date,
              ]),
            },
          };
        },
        {},
      ),
  );

interface FeeChargeEventsSelectProps extends SelectProps<string, true> {
  availableEvents: OutstandingFeeChargeEvent[];
}

export const FeeChargeEventsSelect = React.forwardRef<
  FocusableElement,
  FeeChargeEventsSelectProps
>(({ availableEvents, ...props }, ref) => {
  const { t } = useTranslation(["capitalCall"]);
  return (
    <Select {...props} isMulti={true} ref={ref}>
      {availableEvents.map((event) => (
        <SelectOption
          key={event.id}
          value={event.id}
          labelRight={
            event.amount_outstanding_to_date &&
            t("capitalCall:editor.outstanding", {
              amount: formatMoney(event.amount_outstanding_to_date),
            })
          }
        >
          {event.charged_period_rich_text}
        </SelectOption>
      ))}
    </Select>
  );
});
FeeChargeEventsSelect.displayName = "FeeChargeEventsSelect";
